/**
 * useActivityTracker - Hook to track user activity for real-time admin monitoring
 * Sends heartbeat every 30 seconds with current page and action
 */
import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Map routes to human-readable page names
const PAGE_NAMES = {
  '/': 'Home',
  '/login': 'Login',
  '/register': 'Register',
  '/profile': 'Profile',
  '/settings': 'Settings',
  '/tarot': 'Tarot Reading',
  '/astrology': 'Astrology',
  '/numerology': 'Numerology',
  '/cardology': 'Cardology',
  '/games/rpg': 'Cosmic Quest RPG',
  '/community': 'Community',
  '/feed': 'Community Feed',
  '/chat': 'Global Chat',
  '/admin': 'Admin Panel',
  '/premium': 'Premium',
  '/oracle': 'Oracle',
};

// Get human-readable page name from path
const getPageName = (pathname) => {
  // Exact match
  if (PAGE_NAMES[pathname]) return PAGE_NAMES[pathname];
  
  // Check prefixes
  if (pathname.startsWith('/u/')) return `Profile: ${pathname.split('/')[2]}`;
  if (pathname.startsWith('/games/rpg')) return 'Cosmic Quest RPG';
  if (pathname.startsWith('/admin')) return 'Admin Panel';
  if (pathname.startsWith('/community')) return 'Community';
  
  // Default: capitalize path
  return pathname.split('/').filter(Boolean).map(
    s => s.charAt(0).toUpperCase() + s.slice(1)
  ).join(' > ') || 'Home';
};

export const useActivityTracker = () => {
  const location = useLocation();
  const { token, user } = useAuth();
  const lastPageRef = useRef('');
  const intervalRef = useRef(null);

  const sendHeartbeat = useCallback(async (page, action = 'viewing') => {
    if (!token) return;
    
    try {
      await axios.post(`${API}/api/analytics/heartbeat`, {
        page,
        action,
        details: {
          url: window.location.href,
          title: document.title
        }
      }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 5000
      });
    } catch (error) {
      // Silent fail - don't disrupt user experience
      console.debug('Activity heartbeat failed:', error.message);
    }
  }, [token]);

  // Track page changes
  useEffect(() => {
    const pageName = getPageName(location.pathname);
    
    if (pageName !== lastPageRef.current) {
      lastPageRef.current = pageName;
      sendHeartbeat(pageName, 'navigated');
    }
  }, [location.pathname, sendHeartbeat]);

  // Send heartbeat every 30 seconds
  useEffect(() => {
    if (!token) return;

    // Initial heartbeat
    const pageName = getPageName(location.pathname);
    sendHeartbeat(pageName, 'viewing');

    // Set up interval
    intervalRef.current = setInterval(() => {
      const currentPage = getPageName(location.pathname);
      sendHeartbeat(currentPage, 'active');
    }, 30000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [token, location.pathname, sendHeartbeat]);

  // Track when user leaves
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        sendHeartbeat(getPageName(location.pathname), 'inactive');
      } else {
        sendHeartbeat(getPageName(location.pathname), 'returned');
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [location.pathname, sendHeartbeat]);

  return { sendHeartbeat };
};

export default useActivityTracker;
